import {
  DATE_PRESET_LABEL,
  DATE_PRESETS,
  buildProvidersByGameType,
  datePresetRange,
  displayCell,
  fetchHistoryRecords,
  fetchPublicCategories,
  fetchWcktBets,
  formatMoney,
  profitClass,
  providersForGameType,
  retainProviderIfAvailable,
  websiteCategoryChips,
} from './helpers.js';
import { createBettingHistorySession } from './bettingHistoryLoad.js';
import { BLOCKING_LOAD_STUCK_MS } from '../boot/playerLoading.js';
import { categoryLabel, createPlayerI18n, phrase } from '../i18n/language.js';

export function createBettingRecordsPage({ useState, useEffect, useCallback, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  if (typeof useState !== 'function' || typeof useEffect !== 'function' || typeof useCallback !== 'function') {
    throw new Error('Betting Records requires React hooks.');
  }
  function ClipboardEmpty() {
    return jsxs('div', {
      className: 'br-empty',
      children: [
        jsxs('svg', {
          className: 'br-empty-icon',
          viewBox: '0 0 64 64',
          'aria-hidden': 'true',
          children: [
            jsx('rect', {
              x: '18',
              y: '12',
              width: '28',
              height: '40',
              rx: '3',
              fill: '#3a3a3a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'a'),
            jsx('rect', {
              x: '24',
              y: '8',
              width: '16',
              height: '8',
              rx: '2',
              fill: '#5a5a5a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'b'),
            jsx('path', {
              d: 'M26 28h12M26 36h12M26 44h8',
              fill: 'none',
              stroke: '#8d8d8d',
              strokeWidth: '2',
              strokeLinecap: 'round',
            }, 'c'),
          ],
        }),
        jsx('p', { children: phrase('No Data') }),
      ],
    });
  }

  function FunnelIcon() {
    return jsxs('svg', {
      className: 'br-icon',
      viewBox: '0 0 24 24',
      'aria-hidden': 'true',
      children: [
        jsx('path', {
          d: 'M4 5h16l-6.2 7.4V19l-3.6-2v-4.6L4 5Z',
          fill: 'currentColor',
        }),
      ],
    });
  }

  function Chip({ label, selected, onSelect }) {
    return jsx('button', {
      type: 'button',
      className: selected ? 'br-chip is-on' : 'br-chip',
      'aria-pressed': selected,
      onClick: onSelect,
      children: phrase(categoryLabel(label)),
    });
  }

  function BettingRecordsPage({ onClose }) {
    const { phrase } = usePlayerI18n();
    const [tab, setTab] = useState('settled');
    const [filters, setFilters] = useState({
      providers: [],
      categories: [],
      providersByGameType: {},
    });
    const [applied, setApplied] = useState(() => ({
      provider: 'All',
      gameType: 'All',
      ...datePresetRange('last7'),
    }));
    const [draft, setDraft] = useState(applied);
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState('');
    const [reload, setReload] = useState(0);
    const [session] = useState(() => createBettingHistorySession({
      fetchGames: fetchHistoryRecords,
      fetchSports: fetchWcktBets,
    }));
    const [view, setView] = useState(() => session.snapshot());
    session.setProviders(filters.providersByGameType);

    useEffect(() => session.subscribe(setView), [session]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const publicCategories = await fetchPublicCategories();
          if (cancelled) return;
          const categories = websiteCategoryChips(publicCategories);
          const providersByGameType = await buildProvidersByGameType(publicCategories);
          if (cancelled) return;
          setFilters({
            providers: providersByGameType.All || [],
            categories,
            providersByGameType,
          });
        } catch {
          /* keep empty filter chips; list still loads from existing history APIs */
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      const request = {
        tab,
        page,
        from: applied.from,
        to: applied.to,
        gameType: applied.gameType,
        provider: applied.provider,
        reload,
      };
      session.applyUserQuery(request);
      const stuck = window.setTimeout(() => {
        session.releaseGameLoading();
      }, BLOCKING_LOAD_STUCK_MS);
      return () => {
        window.clearTimeout(stuck);
        session.cancel();
      };
    }, [applied.from, applied.to, applied.gameType, applied.provider, page, tab, reload, session]);

    useEffect(() => {
      session.applyMetadata(filters.providersByGameType);
    }, [filters.providersByGameType, session]);

    const openFilter = () => {
      setDraft(applied);
      setFilterOpen(true);
    };

    const confirmFilter = () => {
      setApplied(draft);
      setPage(1);
      setExpanded('');
      setFilterOpen(false);
    };

    const selectGameType = (gameType) => {
      setDraft((prev) => {
        const available = providersForGameType(gameType, filters.providersByGameType);
        return {
          ...prev,
          gameType,
          provider: retainProviderIfAvailable(prev.provider, available),
        };
      });
    };

    const closePage = () => {
      if (typeof onClose === 'function') onClose();
    };

    const rows = view.rows;
    const totalPages = view.totalPages;
    const loading = view.loading;
    const error = view.error;
    const columns = tab === 'settled'
      ? [phrase('Platform'), phrase('Game Type'), phrase('Turnover'), 'Profit/Loss']
      : [phrase('Platform'), phrase('Game Type'), phrase('Game'), 'Bet'];
    const draftProviders = providersForGameType(draft.gameType, filters.providersByGameType);

    return jsxs('div', {
      className: 'br-page',
      'aria-label': phrase('Betting Records'),
      children: [
        jsxs('header', {
          className: 'br-header',
          children: [
            jsx('h1', { className: 'br-title', children: phrase('Betting Records') }),
            jsx('button', {
              type: 'button',
              className: 'br-close',
              'aria-label': phrase('Close'),
              onClick: closePage,
              children: jsx('svg', {
                className: 'br-icon',
                viewBox: '0 0 24 24',
                'aria-hidden': 'true',
                children: jsx('path', {
                  d: 'M6 6l12 12M18 6L6 18',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                }),
              }),
            }),
          ],
        }),
        jsxs('div', {
          className: 'br-tabs',
          role: 'tablist',
          children: [
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'settled',
              className: tab === 'settled' ? 'br-tab is-on' : 'br-tab',
              onClick: () => {
                setTab('settled');
                setPage(1);
                setExpanded('');
              },
              children: phrase('Settled'),
            }),
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'unsettled',
              className: tab === 'unsettled' ? 'br-tab is-on' : 'br-tab',
              onClick: () => {
                setTab('unsettled');
                setPage(1);
                setExpanded('');
              },
              children: phrase('Unsettled'),
            }),
          ],
        }),
        jsxs('div', {
          className: 'br-toolbar',
          children: [
            jsx('span', {
              className: 'br-date-pill',
              children: phrase(DATE_PRESET_LABEL[applied.preset] || DATE_PRESET_LABEL.last7),
            }),
            jsx('button', {
              type: 'button',
              className: 'br-filter-btn',
              'aria-label': phrase('Betting Record Filter'),
              onClick: openFilter,
              children: jsx(FunnelIcon, {}),
            }),
          ],
        }),
        jsxs('div', {
          className: 'br-cols',
          children: columns.map((label) => jsx('span', { children: label }, label)),
        }),
        error
          ? jsxs('p', {
            className: 'br-error',
            children: [
              error,
              ' ',
              jsx('button', {
                type: 'button',
                onClick: () => setReload((n) => n + 1),
                children: phrase('Retry'),
              }),
            ],
          })
          : null,
        view.sportsError && !error
          ? jsx('p', { className: 'br-sports-error', children: view.sportsError })
          : null,
        loading
          ? jsx('p', { className: 'br-loading account-local-status', children: phrase('Loading…') })
          : error
            ? null
          : rows.length
            ? jsx('ul', {
              className: 'br-list',
              children: rows.map((row) => {
                const open = expanded === row.key;
                const cells = tab === 'settled'
                  ? [
                    displayCell(row.platform),
                    categoryLabel(displayCell(row.gameType)),
                    formatMoney(row.turnover),
                    formatMoney(row.profitLoss),
                  ]
                  : [
                    displayCell(row.platform),
                    categoryLabel(displayCell(row.gameType)),
                    displayCell(row.game),
                    formatMoney(row.betAmount),
                  ];
                const details = [
                  [phrase('Game Name'), displayCell(row.game)],
                  row.betLabel && row.betDetail ? [row.betLabel, row.betDetail] : null,
                  row.betAmount != null ? [phrase('Stake / Bet'), formatMoney(row.betAmount)] : null,
                  row.turnover != null ? [phrase('Turnover'), formatMoney(row.turnover)] : null,
                  row.profitLoss != null ? [phrase('Profit / Loss'), formatMoney(row.profitLoss)] : null,
                  row.result ? [phrase('Result'), row.result] : null,
                  row.status ? [phrase('Status'), phrase(row.status)] : null,
                  row.referenceId ? [phrase('Bet / Reference ID'), row.referenceId] : null,
                ].filter(Boolean);
                return jsxs('li', {
                  className: open ? 'br-row is-open' : 'br-row',
                  children: [
                    jsxs('button', {
                      type: 'button',
                      className: 'br-row-main',
                      'aria-expanded': open,
                      onClick: () => setExpanded(open ? '' : row.key),
                      children: [
                        cells.map((cell, idx) => jsx('span', {
                          className: idx === 3 && tab === 'settled' ? `br-cell ${profitClass(row.profitLoss)}` : 'br-cell',
                          children: cell,
                        }, idx)),
                        jsx('span', { className: 'br-chevron', 'aria-hidden': 'true' }),
                      ],
                    }),
                    open
                      ? jsx('dl', {
                        className: 'br-details',
                        children: details.map(([label, value]) => jsxs('div', {
                          children: [
                            jsx('dt', { children: label }),
                            jsx('dd', {
                              className: label === phrase('Profit / Loss') ? profitClass(row.profitLoss) : '',
                              children: value,
                            }),
                          ],
                        }, label)),
                      })
                      : null,
                  ],
                }, row.key);
              }),
            })
            : view.sportsPending
              ? null
              : jsx(ClipboardEmpty, {}),
        !loading && (page > 1 || page < totalPages)
          ? jsxs('div', {
            className: 'br-pager',
            children: [
              jsx('button', {
                type: 'button',
                disabled: page <= 1,
                onClick: () => {
                  setPage((n) => Math.max(1, n - 1));
                  setExpanded('');
                },
                children: phrase('Previous'),
              }),
              jsxs('span', { children: [phrase('Page'), ' ', page] }),
              jsx('button', {
                type: 'button',
                disabled: page >= totalPages,
                onClick: () => {
                  setPage((n) => n + 1);
                  setExpanded('');
                },
                children: phrase('Next'),
              }),
            ],
          })
          : null,
        filterOpen
          ? jsxs('div', {
            className: 'br-sheet',
            role: 'dialog',
            'aria-label': phrase('Betting Record Filter'),
            children: [
              jsxs('header', {
                className: 'br-sheet-head',
                children: [
                  jsx('button', {
                    type: 'button',
                    className: 'br-back',
                    'aria-label': phrase('Back'),
                    onClick: () => setFilterOpen(false),
                    children: '‹',
                  }),
                  jsx('h2', { children: phrase('Betting Record Filter') }),
                ],
              }),
              jsxs('div', {
                className: 'br-sheet-body',
                children: [
                  jsx('h3', { children: phrase('Game Type') }),
                  jsxs('div', {
                    className: 'br-chips',
                    children: [
                      jsx(Chip, {
                        label: 'All',
                        selected: draft.gameType === 'All',
                        onSelect: () => selectGameType('All'),
                      }),
                      ...filters.categories.map((item) => jsx(Chip, {
                        label: item.label,
                        selected: draft.gameType === item.value,
                        onSelect: () => selectGameType(item.value),
                      }, item.value)),
                    ],
                  }),
                  jsx('h3', { children: phrase('Platform') }),
                  jsxs('div', {
                    className: 'br-chips',
                    children: [
                      jsx(Chip, {
                        label: 'All',
                        selected: draft.provider === 'All',
                        onSelect: () => setDraft((prev) => ({ ...prev, provider: 'All' })),
                      }),
                      ...draftProviders.map((item) => jsx(Chip, {
                        label: item.label,
                        selected: draft.provider === item.value,
                        onSelect: () => setDraft((prev) => ({ ...prev, provider: item.value })),
                      }, item.value)),
                    ],
                  }),
                  jsx('h3', { children: phrase('Date') }),
                  jsx('div', {
                    className: 'br-chips br-chips-date',
                    children: DATE_PRESETS.map((key) => jsx(Chip, {
                      label: DATE_PRESET_LABEL[key],
                      selected: draft.preset === key,
                      onSelect: () => setDraft((prev) => ({ ...prev, ...datePresetRange(key) })),
                    }, key)),
                  }),
                ],
              }),
              jsx('button', {
                type: 'button',
                className: 'br-confirm',
                onClick: confirmFilter,
                children: phrase('Confirm'),
              }),
            ],
          })
          : null,
      ],
    });
  }

  return BettingRecordsPage;
}
